import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getMyAnnualStatement, logAnnualStatementDownload } from "@/lib/hmrc-export.functions";
import { currentTaxYear, taxYearRange, fmtGBP, statementToCsv, statementToJson } from "@/lib/hmrc-shared";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/bartender/annual-statement")({
  component: AnnualStatementPage,
  head: () => ({
    meta: [
      { title: "Annual earnings statement · The Hospitality Reserve" },
      { name: "description", content: "Your yearly earnings breakdown for self-assessment." },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: () => <div className="p-10 text-center">Could not load your statement.</div>,
  notFoundComponent: () => <div className="p-10 text-center">Not found.</div>,
});

function AnnualStatementPage() {
  const [taxYear, setTaxYear] = useState(currentTaxYear());
  const fetchStatement = useServerFn(getMyAnnualStatement);
  const { data, isLoading } = useQuery({
    queryKey: ["annual-statement", taxYear],
    queryFn: () => fetchStatement({ data: { taxYear } }),
  });

  const statement = data && !("error" in data) ? data : null;

  const logDownload = useServerFn(logAnnualStatementDownload);

  const downloadAs = (format: "csv" | "json") => {
    if (!statement) return;
    const content = format === "json" ? statementToJson(statement) : statementToCsv(statement);
    const mime = format === "json" ? "application/json" : "text/csv";
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `earnings-statement-${statement.tax_year_label.replace("/", "-")}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    void logDownload({ data: { taxYear, format } });
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-4xl px-6 py-10 print:py-0">
        <Link to="/bartender" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-accent print:hidden">← Back</Link>
        <p className="mt-6 tracking-wide-luxury text-accent">Annual statement</p>
        <h1 className="mt-2 font-display text-3xl">Your earnings, tax year {taxYearRange(taxYear).label}</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          A breakdown of everything you earned through The Hospitality Reserve between 6 April and
          5 April. Keep this for your Self Assessment — as a UK digital platform we report the same
          figures to HMRC.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3 print:hidden">
          <select
            aria-label="Tax year"
            value={taxYear}
            onChange={(e) => setTaxYear(Number(e.target.value))}
            className="border border-border bg-background px-3 py-2 text-sm"
          >
            {Array.from({ length: currentTaxYear() - 2019 }, (_, i) => currentTaxYear() - i).map((y) => (
              <option key={y} value={y}>{taxYearRange(y).label}</option>
            ))}
          </select>
          <Button variant="outline" disabled={!statement} onClick={() => downloadAs("csv")}>Download CSV</Button>
          <Button variant="outline" disabled={!statement} onClick={() => downloadAs("json")}>Download JSON</Button>
          <Button variant="outline" disabled={!statement} onClick={() => { void logDownload({ data: { taxYear, format: "print" } }); window.print(); }}>Print / PDF</Button>
        </div>

        {isLoading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}
        {data && "error" in data && <p className="mt-8 text-sm text-destructive">{data.error}</p>}

        {statement && (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-4">
              <Stat label="Total earned" value={fmtGBP(statement.total_pence)} />
              <Stat label="Paid out" value={fmtGBP(statement.paid_pence)} />
              <Stat label="Awaiting payout" value={fmtGBP(statement.pending_pence)} />
              <Stat label="Shifts worked" value={String(statement.shift_count)} />
            </div>

            <div className="mt-6 rounded-md border border-border bg-card p-6 text-sm">
              <h2 className="tracking-wide-luxury text-[0.65rem] uppercase text-muted-foreground">Details on record</h2>
              <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                <Row label="Full legal name" value={statement.worker.full_name} />
                <Row label="Date of birth" value={statement.worker.date_of_birth} />
                <Row label="Home address" value={[statement.worker.address, statement.worker.postcode].filter(Boolean).join(", ")} />
              </dl>
              <p className="mt-3 text-xs text-muted-foreground">
                Something wrong? Update it in your account settings or contact support.
              </p>
            </div>

            <div className="mt-6 overflow-x-auto rounded-md border border-border bg-card">
              <table className="w-full text-sm">
                <caption className="sr-only">Earnings lines for tax year {statement.tax_year_label}</caption>
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide-luxury text-muted-foreground">
                    <th scope="col" className="p-3">Date</th>
                    <th scope="col" className="p-3">Venue</th>
                    <th scope="col" className="p-3">Role</th>
                    <th scope="col" className="p-3">Type</th>
                    <th scope="col" className="p-3">Status</th>
                    <th scope="col" className="p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.lines.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No earnings recorded in this tax year.</td></tr>
                  )}
                  {statement.lines.map((l, i) => (
                    <tr key={`${l.shift_id ?? "x"}-${i}`} className="border-b border-border/50 last:border-0">
                      <td className="p-3">{l.date}</td>
                      <td className="p-3">{l.venue ?? "—"}</td>
                      <td className="p-3">{l.role ?? "—"}</td>
                      <td className="p-3">{l.kind.replace(/_/g, " ")}</td>
                      <td className="p-3">{l.status.replace(/_/g, " ")}</td>
                      <td className="p-3 text-right">{fmtGBP(l.amount_pence)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <p className="tracking-wide-luxury text-[0.6rem] uppercase text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-2xl">{value}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs tracking-wide-luxury text-muted-foreground">{label}</dt>
      <dd>{value || <span className="text-muted-foreground">Not provided</span>}</dd>
    </div>
  );
}
